import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface FooterProps {
  currentLang?: 'sl' | 'en';
}

export default function Footer({ currentLang = 'sl' }: FooterProps) {
  const partnerLogos = [
    { id: 'logo_1', src: '/logos/logo1.webp', alt: 'Partner logo 1' },
    { id: 'logo_2', src: '/logos/logo2.webp', alt: 'Partner logo 2' },
    { id: 'logo_3', src: '/logos/logo3.webp', alt: 'Partner logo 3' },
  ];

  const footerLinks = {
    products: {
      id: 'footer_group_products',
      title: currentLang === 'sl' ? 'Produkti' : 'Products',
      links: [
        {
          id: 'footer_link_honey',
          href: '/products',
          label: currentLang === 'sl' ? 'Med' : 'Honey',
        },
        {
          id: 'footer_link_pollen',
          href: '/products',
          label: currentLang === 'sl' ? 'Cvetni prah' : 'Pollen',
        },
        {
          id: 'footer_link_liqueur',
          href: '/products',
          label: currentLang === 'sl' ? 'Medeni liker' : 'Honey Liqueur',
        },
      ],
    },
    services: {
      id: 'footer_group_services',
      title: currentLang === 'sl' ? 'Storitve' : 'Services',
      links: [
        {
          id: 'footer_link_apitherapy',
          href: '/services',
          label: currentLang === 'sl' ? 'Apiterapija' : 'Apitherapy',
        },
        {
          id: 'footer_link_learning',
          href: '/services',
          label: currentLang === 'sl' ? 'Učna pot' : 'Learning Path',
        },
        {
          id: 'footer_link_accommodation',
          href: '/services',
          label: currentLang === 'sl' ? 'Nastanitve' : 'Accommodation',
        },
      ],
    },
    info: {
      id: 'footer_group_info',
      title: currentLang === 'sl' ? 'Informacije' : 'Information',
      links: [
        {
          id: 'footer_link_about',
          href: '/homepage#about',
          label: currentLang === 'sl' ? 'O nas' : 'About Us',
        },
        {
          id: 'footer_link_blog',
          href: '/homepage#blog',
          label: currentLang === 'sl' ? 'Blog' : 'Blog',
        },
        {
          id: 'footer_link_contact',
          href: '/homepage#contact',
          label: currentLang === 'sl' ? 'Kontakt' : 'Contact',
        },
      ],
    },
  };

  const socialLinks = [
    { id: 'social_facebook', icon: 'facebook', href: '#facebook', label: 'Facebook' },
    { id: 'social_instagram', icon: 'instagram', href: '#instagram', label: 'Instagram' },
  ];

  return (
    <>
      <div className="px-6 md:px-10 py-10 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 items-center">
            {partnerLogos.map((logo, index) => (
              <div
                key={logo.id}
                className={`${index === 0 ? 'h-16' : 'h-20 md:h-24'} rounded-xl bg-white/70 flex items-center justify-center p-4`}
              >
                <AppImage
                  src={logo.src}
                  alt={logo.alt}
                  width={220}
                  height={90}
                  className="max-h-full w-auto object-contain"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      <footer className="py-20 px-6 md:px-10 border-t border-border bg-white">
        <div className="max-w-7xl mx-auto">
          {/* Link Groups */}
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            {/* Products */}
            <div className="space-y-4">
              <p className="text-xs font-bold uppercase tracking-widest opacity-40">
                {footerLinks.products.title}
              </p>
              <div className="flex flex-col gap-3">
                {footerLinks.products.links.map((link) => (
                  <Link
                    key={link.id}
                    href={link.href}
                    className="text-sm font-medium text-foreground hover:text-primary transition-colors"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Services */}
            <div className="space-y-4">
              <p className="text-xs font-bold uppercase tracking-widest opacity-40">
                {footerLinks.services.title}
              </p>
              <div className="flex flex-col gap-3">
                {footerLinks.services.links.map((link) => (
                  <Link
                    key={link.id}
                    href={link.href}
                    className="text-sm font-medium text-foreground hover:text-primary transition-colors"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Info */}
            <div className="space-y-4">
              <p className="text-xs font-bold uppercase tracking-widest opacity-40">
                {footerLinks.info.title}
              </p>
              <div className="flex flex-col gap-3">
                {footerLinks.info.links.map((link) => (
                  <Link
                    key={link.id}
                    href={link.href}
                    className="text-sm font-medium text-foreground hover:text-primary transition-colors"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Bottom Row */}
          <div className="flex flex-col md:flex-row justify-between items-center gap-6 pt-8 border-t border-border">
            {/* Copyright */}
            <p className="text-xs text-muted-foreground">© Apis Pan 2026</p>

            {/* Social Icons */}
            <div className="flex items-center gap-6">
              {socialLinks.map((social) => (
                <a
                  key={social.id}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-primary transition-colors"
                  aria-label={social.label}
                >
                  <Icon name="GlobeAltIcon" size={20} />
                </a>
              ))}
            </div>

            {/* Legal */}
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <Link href="#privacy" className="hover:text-primary transition-colors">
                {currentLang === 'sl' ? 'Zasebnost' : 'Privacy'}
              </Link>
              <span>·</span>
              <Link href="#terms" className="hover:text-primary transition-colors">
                {currentLang === 'sl' ? 'Pogoji' : 'Terms'}
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
